import { Button, Modal, type Column } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { JobRun, JobType } from "../../../api/syncers.service.js";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { useSyncerRuns, useTriggerSyncerJob } from "../../../hooks/useSyncerRuns.js";
import LogViewer from "./LogViewer.js";
import styles from "./Syncers.module.css";

const POLL_INTERVAL = 5000; // 5 seconds

type TabConfig = {
  id: JobType;
  label: string;
  description: string;
};

const TABS: TabConfig[] = [
  {
    id: "testimonials-migration",
    label: "Testimonials Migration",
    description: "Migrates testimonials from SQLite snapshot to PostgreSQL (one-time job)",
  },
];

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return "-";
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;
  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString();
}

type StatusBadgeProps = {
  status: string;
};

function StatusBadge({ status }: StatusBadgeProps) {
  const getStatusClass = () => {
    switch (status) {
      case "completed":
        return styles.statusCompleted;
      case "failed":
        return styles.statusFailed;
      case "running":
        return styles.statusRunning;
      case "pending":
        return styles.statusPending;
      default:
        return styles.statusDefault;
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass()}`}>{status.toUpperCase()}</span>
  );
}

type TriggerTypeBadgeProps = {
  triggerType: string;
};

function TriggerTypeBadge({ triggerType }: TriggerTypeBadgeProps) {
  const getTriggerTypeClass = () => {
    switch (triggerType) {
      case "manual":
        return styles.triggerManual;
      case "scheduled":
        return styles.triggerScheduled;
      case "webhook":
        return styles.triggerWebhook;
      default:
        return styles.triggerDefault;
    }
  };

  return (
    <span className={`${styles.triggerBadge} ${getTriggerTypeClass()}`}>
      {triggerType.toUpperCase()}
    </span>
  );
}

type SyncerTabContentProps = {
  jobType: JobType;
  tabConfig: TabConfig;
  onViewLogs: (run: JobRun) => void;
};

function SyncerTabContent({ jobType, tabConfig, onViewLogs }: SyncerTabContentProps) {
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data, loading, error, refetch, isRefetching } = useSyncerRuns({
    jobType,
    limit: 50,
  });
  const { mutate: triggerJob, loading: triggering } = useTriggerSyncerJob();

  const hasRunningJob = useMemo(() => {
    return data?.runs?.some(run => run.status === "running" || run.status === "pending") ?? false;
  }, [data]);

  useEffect(() => {
    if (!hasRunningJob) return;

    const interval = setInterval(() => {
      refetch();
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [hasRunningJob, refetch]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleTriggerSync = useCallback(async () => {
    try {
      await triggerJob({ jobType, triggeredBy: "manual" });
      setNotification({
        type: "success",
        message: `${tabConfig.label} job triggered successfully`,
      });
      refetch();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to trigger sync job";
      setNotification({ type: "error", message: errorMessage });
    }
  }, [triggerJob, jobType, tabConfig.label, refetch]);

  const handleRowClick = useCallback(
    (run: JobRun) => {
      onViewLogs(run);
    },
    [onViewLogs],
  );

  const columns: Column<JobRun>[] = useMemo(
    () => [
      {
        header: "Status",
        accessor: "status" as keyof JobRun,
        render: (value: unknown) => <StatusBadge status={value as string} />,
      },
      {
        header: "Started At",
        accessor: "started_at" as keyof JobRun,
        render: (value: unknown) => formatDateTime(value as string | null),
      },
      {
        header: "Duration",
        accessor: "started_at" as keyof JobRun,
        render: (_value: unknown, row: JobRun) => formatDuration(row.started_at, row.completed_at),
      },
      {
        header: "Trigger Type",
        accessor: "trigger_type" as keyof JobRun,
        render: (value: unknown) => <TriggerTypeBadge triggerType={value as string} />,
      },
      {
        header: "Actions",
        accessor: "id" as keyof JobRun,
        render: (_value: unknown, row: JobRun) => (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={event => {
              event.stopPropagation();
              handleRowClick(row);
            }}
          >
            View Logs
          </Button>
        ),
      },
    ],
    [handleRowClick],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  function renderTableContent() {
    if (loading && !isRefetching) {
      return <LoadingState message="Loading job runs..." />;
    }

    if (error) {
      return (
        <EmptyState
          variant="error"
          title="Failed to load job runs"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => refetch(),
          }}
        />
      );
    }

    if (!data?.runs?.length) {
      return (
        <EmptyState
          variant="empty"
          title="No job runs found"
          message={`No ${tabConfig.label} jobs have been run yet. Click 'Trigger Sync' to start a new job.`}
        />
      );
    }

    return (
      <div className={styles.tableContainer}>
        <DataTable
          data={data.runs}
          columns={columns}
          onRowClick={handleRowClick}
          rowKey="id"
          size="small"
        />
      </div>
    );
  }

  return (
    <>
      {notification && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
          <button
            type="button"
            onClick={() => setNotification(null)}
            className={styles.notificationClose}
            aria-label="Dismiss notification"
          >
            x
          </button>
        </div>
      )}

      <div className={styles.tabDescription}>{tabConfig.description}</div>

      <div className={styles.tabHeader}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTriggerSync}
          disabled={triggering || hasRunningJob}
        >
          {triggering ? "Triggering..." : "Trigger Sync"}
        </Button>
        {hasRunningJob && <span className={styles.runningIndicator}>Job in progress...</span>}
      </div>

      {renderTableContent()}
    </>
  );
}

function SyncersPage() {
  const [activeTab, setActiveTab] = useState<JobType>("testimonials-migration");
  const [selectedRun, setSelectedRun] = useState<JobRun | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewLogs = useCallback((run: JobRun) => {
    setSelectedRun(run);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const activeTabConfig = TABS.find(tab => tab.id === activeTab) ?? TABS[0];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Syncers" subtitle="Manage and monitor data synchronization jobs" />

      <div className={styles.content}>
        <div className={styles.tabs}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.tabContent}>
          <SyncerTabContent
            key={activeTab}
            jobType={activeTab}
            tabConfig={activeTabConfig}
            onViewLogs={handleViewLogs}
          />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={`Job Run ${selectedRun?.id ?? ""}`}
        size="large"
        blurBackground
      >
        <LogViewer run={selectedRun} />
      </Modal>
    </div>
  );
}

export default SyncersPage;
